import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";
import { brandColors } from "../theme";
import { alpha } from "@mui/material/styles";
import { T } from "./Translate";
import PageLayout from "./PageLayout";

const formatMoney = (value) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

const formatDateShort = (date) =>
  date.toLocaleDateString(undefined, { day: "numeric", month: "short" });

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Serie diaria de ingresos, últimos `days` días (incluye hoy, con ceros en
// los días sin ventas para que el eje no se salte fechas).
const buildDailySeries = (sales, days) => {
  const buckets = new Map();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    buckets.set(d.toDateString(), { date: d, total: 0 });
  }

  sales.forEach((sale) => {
    const d = toDate(sale.purchaseDate);
    if (!d) return;
    const key = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toDateString();
    const bucket = buckets.get(key);
    if (bucket) bucket.total += Number(sale.totalAmount) || 0;
  });

  return Array.from(buckets.values());
};

const RevenueBarChart = ({ series }) => {
  const [hovered, setHovered] = useState(null);
  const width = 100;
  const height = 160;
  const barGap = 2;
  const max = Math.max(1, ...series.map((s) => s.total));
  const barSlot = width / series.length;
  const barWidth = Math.max(2, barSlot - barGap);
  const baselineY = height - 24;
  const plotHeight = baselineY - 10;
  const ticks = [0, max / 2, max];

  return (
    <Box sx={{ position: "relative" }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={200} preserveAspectRatio="none" role="img" aria-label="Ingresos por día">
        {ticks.map((t, i) => {
          const y = baselineY - (t / max) * plotHeight;
          return (
            <line
              key={i}
              x1={0}
              x2={width}
              y1={y}
              y2={y}
              stroke={brandColors.mode === "light" ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.12)"}
              strokeWidth={0.3}
            />
          );
        })}
        {series.map((s, i) => {
          const barHeight = (s.total / max) * plotHeight;
          const x = i * barSlot + barGap / 2;
          const y = baselineY - barHeight;
          const isHovered = hovered === i;
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(barHeight, s.total > 0 ? 1 : 0)}
                rx={1}
                fill={isHovered ? brandColors.accent : alpha(brandColors.accent, 0.55)}
                style={{ cursor: s.total > 0 ? "pointer" : "default", transition: "fill 0.15s ease" }}
              />
              <rect
                x={i * barSlot}
                y={10}
                width={barSlot}
                height={plotHeight}
                fill="transparent"
                onPointerEnter={() => setHovered(i)}
                onPointerLeave={() => setHovered((h) => (h === i ? null : h))}
              />
            </g>
          );
        })}
      </svg>
      {hovered != null && (
        <Box
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            bgcolor: brandColors.backgroundDefault,
            border: `1px solid ${brandColors.divider}`,
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
            pointerEvents: "none",
          }}
        >
          <Typography variant="caption" sx={{ color: brandColors.textSecondary, display: "block" }}>
            {formatDateShort(series[hovered].date)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: brandColors.textPrimary }}>
            {formatMoney(series[hovered].total)}
          </Typography>
        </Box>
      )}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {formatDateShort(series[0].date)}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {formatDateShort(series[series.length - 1].date)}
        </Typography>
      </Stack>
    </Box>
  );
};

const StatCard = ({ label, value }) => (
  <Paper
    variant="outlined"
    sx={{ p: { xs: 1.75, sm: 2.5 }, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider", height: "100%", minWidth: 0 }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", overflowWrap: "break-word" }}>
      {label}
    </Typography>
    <Typography
      variant="h4"
      sx={{ fontWeight: 700, mt: 0.5, fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.125rem" }, overflowWrap: "break-word" }}
    >
      {value}
    </Typography>
  </Paper>
);

const PUBLICATION_TYPES = {
  offer: { label: <T>Oferta</T> },
  event: { label: <T>Evento</T> },
  car: { label: <T>Auto</T> },
  place: { label: <T>Propiedad</T> },
};

const Statistics = () => {
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [events, setEvents] = useState([]);
  const [cars, setCars] = useState([]);
  const [places, setPlaces] = useState([]);
  const [sales, setSales] = useState([]);
  const [rangeDays, setRangeDays] = useState(14);

  useEffect(() => {
    let active = true;
    const user = auth.currentUser;
    if (!user) return undefined;

    const load = async () => {
      try {
        const [offersSnap, eventsSnap, carsSnap, placesSnap, salesSnap] = await Promise.all([
          getDocs(query(collection(db, "offers"), where("uid", "==", user.uid))),
          getDocs(query(collection(db, "events"), where("uid", "==", user.uid))),
          getDocs(query(collection(db, "cars"), where("uid", "==", user.uid))),
          getDocs(query(collection(db, "places"), where("uid", "==", user.uid))),
          getDocs(query(collection(db, "sales"), where("sellerId", "==", user.uid))),
        ]);
        if (!active) return;
        setOffers(offersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setEvents(eventsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setCars(carsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPlaces(placesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setSales(salesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Error al cargar estadísticas:", error);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const stats = useMemo(() => {
    const totalRevenue = sales.reduce((acc, s) => acc + (Number(s.totalAmount) || 0), 0);
    const totalOrders = sales.length;
    const avgOrder = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    const discounts = offers
      .map((o) => Number(o.offer))
      .filter((n) => !Number.isNaN(n));
    const avgDiscount = discounts.length > 0
      ? discounts.reduce((acc, n) => acc + n, 0) / discounts.length
      : 0;

    return {
      totalPublications: offers.length + events.length + cars.length + places.length,
      totalRevenue,
      totalOrders,
      avgOrder,
      avgDiscount,
    };
  }, [offers, events, cars, places, sales]);

  const dailySeries = useMemo(() => buildDailySeries(sales, rangeDays), [sales, rangeDays]);

  const recentPublications = useMemo(() => {
    const all = [
      ...offers.map((o) => ({ id: o.id, title: o.product, type: "offer", createdAt: o.createdAt })),
      ...events.map((e) => ({ id: e.id, title: e.title, type: "event", createdAt: e.createdAt })),
      ...cars.map((c) => ({ id: c.id, title: c.title, type: "car", createdAt: c.createdAt })),
      ...places.map((p) => ({ id: p.id, title: p.title, type: "place", createdAt: p.createdAt })),
    ];
    return all
      .sort((a, b) => {
        const dateA = toDate(a.createdAt)?.getTime() || 0;
        const dateB = toDate(b.createdAt)?.getTime() || 0;
        return dateB - dateA;
      })
      .slice(0, 10);
  }, [offers, events, cars, places]);

  return (
    <PageLayout title={<T>Estadísticas</T>} contentSx={{ mt: "16px" }}>
      {loading ? (
          <Typography variant="body2" sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>
            <T>Cargando estadísticas...</T>
          </Typography>
        ) : (
          <Stack spacing={2.5}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, width: "100%" }}>
              <StatCard label={<T>Publicaciones totales</T>} value={stats.totalPublications} />
              <StatCard label={<T>Ofertas</T>} value={offers.length} />
              <StatCard label={<T>Eventos</T>} value={events.length} />
              <StatCard label={<T>Autos</T>} value={cars.length} />
              <StatCard label={<T>Propiedades / viajes</T>} value={places.length} />
              <StatCard label={<T>Descuento promedio</T>} value={`${stats.avgDiscount.toFixed(0)}%`} />
              <StatCard label={<T>Ventas totales</T>} value={formatMoney(stats.totalRevenue)} />
              <StatCard label={<T>Pedidos</T>} value={stats.totalOrders} />
              <Box sx={{ gridColumn: "1 / -1" }}>
                <StatCard label={<T>Ticket promedio</T>} value={formatMoney(stats.avgOrder)} />
              </Box>
            </Box>

            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  <T>Ingresos por día</T>
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={rangeDays}
                  onChange={(e, value) => value && setRangeDays(value)}
                >
                  <ToggleButton value={7}>7D</ToggleButton>
                  <ToggleButton value={14}>14D</ToggleButton>
                  <ToggleButton value={30}>30D</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              {stats.totalOrders === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>
                  <T>Todavía no hay ventas para graficar.</T>
                </Typography>
              ) : (
                <RevenueBarChart series={dailySeries} />
              )}
            </Paper>

            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                <T>Últimas publicaciones</T>
              </Typography>
              {recentPublications.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <T>Todavía no hay publicaciones.</T>
                </Typography>
              ) : (
                <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />} spacing={1.5}>
                  {recentPublications.map((p) => {
                    const d = toDate(p.createdAt);
                    return (
                      <Stack key={p.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1.5 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {p.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {d ? d.toLocaleDateString() : "—"}
                          </Typography>
                        </Box>
                        <Chip size="small" label={PUBLICATION_TYPES[p.type]?.label} />
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Paper>
        </Stack>
      )}
    </PageLayout>
  );
};

export default Statistics;
